import Button from "@/components/common/button/Button";
import UserDatalist from "@/components/entities/app/user/UserDatalist";
import UserStatusView from "@/components/entities/app/user/UserStatusView";
import ModalPortal from "@/components/modal/ModalPortal";
import { faCodeMerge, faPen } from "@fortawesome/free-solid-svg-icons";
import { ComponentStateTypes } from "components/common/ComponentState";
import If from "components/common/If";
import Translate from "components/common/Translate";
import Page, { PageActionsType } from "components/common/page/Page";
import PropertyView from "components/common/views/PropertyView";
import { api, graph } from "config/axios-context";
import { AppRoutes, resolveRoute } from "config/router";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { NotNull } from "types/common-types";
import {
  Statuses,
  UserViewDocument,
  UserViewQuery,
  UserViewQueryVariables,
} from "types/graphql";
import useAppContext from "utilities/hooks/useAppContext";

type UserViewType = NotNull<UserViewQuery>["user"];

const MERGE_USER_MODAL_NAME = "mergeUserModal";

const UserViewPage = () => {
  const [state, setState] = useState<ComponentStateTypes>("loading");
  const [isRequestSent, setIsRequestSent] = useState(false);
  const [model, setModel] = useState<UserViewType>();
  const params = useParams<{ id: string }>();
  const context = useAppContext();

  const pageActions: PageActionsType = [
    {
      icon: faPen,
      link:
        context.user.id === Number(params.id)
          ? AppRoutes.EDIT_PROFILE
          : resolveRoute(AppRoutes.USER_EDIT, { id: params.id }),
      name: "edit",
      title: context.t("button.edit"),
    },
    {
      icon: faCodeMerge,
      name: "merge",
      title: context.t("button.merge"),
      link() {
        context.modal.open(MERGE_USER_MODAL_NAME);
      },
    },
  ];

  useEffect(() => {
    loadData().then();
  }, [params.id]);

  const loadData = async () => {
    setState("loading");
    try {
      const response = await graph<UserViewQuery, UserViewQueryVariables>({
        query: UserViewDocument,
        variables: {
          id: Number(params.id),
        },
      });

      if (response.errors?.length) {
        throw response.errors;
      }

      setState("");
      setModel(response.data?.user);
    } catch (error) {
      setState("error");
      context.notify("error", "error.loading-data");
      throw error;
    }
  };

  const changeState = async (newState: Statuses) => {
    try {
        setIsRequestSent(true);
        const response = await api.patch('/user/changeStatus', {
            userId: Number(params.id),
            status: newState,
        });

        if (response.success) {
            await loadData();
        }
    } catch (error: any) {
        context.notify('error', 't1', Array.isArray(error) ? error[0].message : error.message);
        
        throw error;
    } finally {
        setIsRequestSent(false);
    }
  }

  return (
    <>
      <Page title="pages.users.view" state={state} actions={pageActions}>
        <div>
          <PropertyView label="fields.id.name" value={model?.id} />
          <PropertyView label="fields.name.name" value={model?.name} />
          <PropertyView label="fields.username.name" value={model?.username} />
          <PropertyView
            label="fields.state.name"
            value={
              <>
                <UserStatusView status={model?.status || Statuses.Inactive} />
                {model?.id !== context.user.id && (
                    <div className="grid grid-cols-2 gap-4 w-max">
                        <Button disabled={isRequestSent} theme="default primary" title={context.t("status.ACTIVE")} onClick={() => changeState(Statuses.Active)}>
                            <Translate i18nKey="status.ACTIVE" />
                        </Button>
                        <Button disabled={isRequestSent} theme="danger" title={context.t("status.INACTIVE")} onClick={() => changeState(Statuses.Inactive)}>
                            <Translate i18nKey="status.INACTIVE" />
                        </Button>
                    </div>
                )}
              </>
            }
          />
          <PropertyView
            label="fields.roles.name"
            value={model?.roles?.items?.map((c) => c!.role) || "-"}
          />
          <PropertyView label="common.authProviders">
            <>
              <If condition={model?.authData?.items?.length}>
                <ul>
                  {model?.authData?.items?.map((c) => {
                    return (
                      <li key={c!.identifier}>
                        {" "}
                        - {c?.identifier} - {c?.provider}
                      </li>
                    );
                  })}
                </ul>
              </If>
              <If condition={!model?.authData?.items?.length}>
                <>-</>
              </If>
            </>
          </PropertyView>
        </div>
      </Page>
      <ModalPortal name={MERGE_USER_MODAL_NAME} title={"Merge User"}>
        <UserDatalist 
          fixedFilters={{
            id: {
              neq: Number(params.id)
            }
          }}
          onItemClick={{
            type: 'click',
            async onClick(item) {
              const answer = confirm(`${context.t("entities.user.merge.mergeConfirmation")} ${item?.name} (${item?.username})?`);
              if(answer) {
                const response = await api.post('user/merge', {
                  sourceId: Number(params.id),
                  targetId: item.id
                });

                if (response.success) {
                  context.notify('success', context.t("entities.user.merge.mergeSuccess"));
                  context.navigate(AppRoutes.USER_VIEW, { id: Number(item.id) });
                } else {
                  context.notify('error', context.t("entities.user.merge.mergeFailure"));
                }
              }
            }
          }}
        />
      </ModalPortal>
    </>
  );
};

export default UserViewPage;
